import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { Dialog } from '@components/dialog';
import { Table } from '@components/table/Table';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { LazyJsonViewer } from '@components/json/LazyJsonViewer';
import { Button } from '@components/button';

import { useSearchQuoteRatingsNonInfinite, useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { RatingSummaryModal } from '@pages/QuoteDetails/sidebarItems/summary/modals/RatingSummaryModal/RatingSummaryModal';
import { formatCurrency } from '@utils/number';
import type { QuoteRating } from '@models/quoteRating';
import { QuoteRatingSearchRequestQuery } from '@lio/api-types';
import { TQuoteApiResponse } from '@models/quotes';
import { formatDateToTimezone, TwelveHourDateFormat } from '@utils/date';

import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { quoteRatingQueryKey, useCreateQuoteRating } from '@services/quoteRating';
import { NewRateChangeAnalysisContent } from './NewRateChangeAnalysisModal';
import { ToastType } from '@components/toast';

export interface RateChangeAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string;
  policyNumber?: string;
  quote?: TQuoteApiResponse;
}

type TRateChangeTable = {
  items: QuoteRating[];
  params: {
    [x: string]: any;
    sort?: string | undefined;
    pageLimit?: number | undefined;
    pageOffset?: number | undefined;
  };
};

type ModalView = 'list' | 'create';

export const RateChangeAnalysisModal = ({
  isOpen,
  onClose,
  quoteId,
  policyNumber,
  quote,
}: RateChangeAnalysisModalProps) => {
  const [selectedRating, setSelectedRating] = useState<QuoteRating | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [detailsData, setDetailsData] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [view, setView] = useState<ModalView>('list');
  const [isCreateFormValid, setIsCreateFormValid] = useState(false);
  const getRateVersionRef = useRef<() => number | undefined>(() => undefined);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const queryClient = useQueryClient();
  const { mutateAsync: createRateChangeAnalysis, isPending: isCreating } = useCreateQuoteRating();

  // Poll for specific rating by ID
  const { refetch: refetchPollingRating } = useSearchQuoteRatings({
    quoteId,
    enabled: false,
    params: {
      pageLimit: 1,
      hasRateChangeAnalysis: true,
      sort: '-createdAt',
    },
  });

  const handleRatingClick = useCallback((rating: QuoteRating) => {
    setSelectedRating(rating);
    setShowRatingModal(true);
  }, []);

  const handleCloseRatingModal = useCallback(() => {
    setShowRatingModal(false);
    setSelectedRating(null);
  }, []);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const pollForRatingCompletion = useCallback(
    async (ratingId: string) => {
      const pollInterval = 3000; // 3 seconds

      let isComplete = false;

      const performPoll = async () => {
        try {
          const result = await refetchPollingRating();
          const ratings = result.data?.pages?.[0]?.items || [];
          const targetRating = ratings.find((r) => r.id === ratingId);

          if (!targetRating) {
            // Rating not found yet, continue polling
            return;
          }

          const status = targetRating.status?.toLowerCase();

          if (status === 'succeeded') {
            // Rating completed successfully
            isComplete = true;
            setIsPolling(false);

            // Clear and refetch the main table data
            queryClient.invalidateQueries({
              queryKey: quoteRatingQueryKey.searchWithoutInfiniteParams(quoteId, { hasRateChangeAnalysis: true }),
            });

            enqueueSnackbar('Rate change analysis completed successfully', {
              variant: ToastType.Success,
            });

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          } else if (status === 'failed') {
            // Rating failed
            isComplete = true;
            setIsPolling(false);

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }

            enqueueSnackbar('Rate change analysis failed. Please try again.', {
              variant: 'error',
            });

            // Still refresh the table to show the failed status
            queryClient.invalidateQueries({
              queryKey: quoteRatingQueryKey.searchWithoutInfiniteParams(quoteId, { hasRateChangeAnalysis: true }),
            });
          }
        } catch (error) {
          console.error('Error polling for rating completion:', error);
          // Continue polling on error
        }
      };

      // Start polling
      setIsPolling(true);

      // Perform first poll immediately
      await performPoll();

      // Set up interval for subsequent polls if not complete
      if (!isComplete) {
        pollingIntervalRef.current = setInterval(performPoll, pollInterval);
      }
    },
    [refetchPollingRating, quoteId, queryClient],
  );

  const formatPercentage = useCallback((percentage: number | null | undefined) => {
    if (percentage === null || percentage === undefined) return 'N/A';
    const percentValue = percentage * 100;
    const sign = percentValue >= 0 ? '+' : '';
    return `${sign}${percentValue.toFixed(2)}%`;
  }, []);

  const handleViewDetails = useCallback((details: string) => {
    try {
      const parsedDetails = JSON.parse(details);
      setDetailsData(parsedDetails);
      setShowDetailsModal(true);
    } catch (e) {
      console.error('Failed to parse details JSON:', e);
      setDetailsData({ error: 'Invalid JSON format', raw: details });
      setShowDetailsModal(true);
    }
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setDetailsData(null);
  }, []);

  const handleCreateNew = async (userEnteredRateChangeAnalysisRateVersion?: number): Promise<boolean> => {
    try {
      let rateVersionParams: {
        rateChangeAnalysisStartingRateVersion?: number;
        rateChangeAnalysisRateVersion?: number;
      } = {};

      // the earlier rate version should be the starting rate version, and the later rate version should be the rate version to compared to
      if (userEnteredRateChangeAnalysisRateVersion !== undefined && quote?.rateVersion != null) {
        const currentRateVersion = quote.rateVersion;
        if (userEnteredRateChangeAnalysisRateVersion <= currentRateVersion) {
          rateVersionParams = {
            rateChangeAnalysisStartingRateVersion: userEnteredRateChangeAnalysisRateVersion,
            rateChangeAnalysisRateVersion: currentRateVersion,
          };
        } else {
          rateVersionParams = {
            rateChangeAnalysisStartingRateVersion: currentRateVersion,
            rateChangeAnalysisRateVersion: userEnteredRateChangeAnalysisRateVersion,
          };
        }
      } else if (userEnteredRateChangeAnalysisRateVersion !== undefined) {
        rateVersionParams = {
          rateChangeAnalysisRateVersion: userEnteredRateChangeAnalysisRateVersion,
        };
      }

      const response = await createRateChangeAnalysis(
        {
          quoteId,
          enableRateChangeAnalysis: true,
          ...rateVersionParams,
        },
        {
          onError: async (error) => {
            const data = await error?.response?.json();

            console.error(data?.error.message ?? 'Failed to create rate change analysis:', error);
            enqueueSnackbar(data?.error.message ?? 'Failed to create rate change analysis', {
              variant: 'error',
            });
          },
        },
      );

      if (response?.id) {
        // Start polling for the newly created rating
        await pollForRatingCompletion(response.id);
      } else {
        // If no ID returned, just invalidate queries
        queryClient.invalidateQueries({
          queryKey: quoteRatingQueryKey.searchWithoutInfiniteParams(quoteId, { hasRateChangeAnalysis: true }),
        });

        enqueueSnackbar('Rate change analysis started successfully', {
          variant: ToastType.Success,
        });
      }

      return true;
    } catch (error: any) {
      return false;
    }
  };

  const handleCreateSubmit = async () => {
    const rateVersion = getRateVersionRef.current();
    if (rateVersion !== undefined) {
      const success = await handleCreateNew(rateVersion);
      if (success) {
        setView('list');
      }
    }
  };

  const handleFormValidityChange = useCallback((isValid: boolean) => {
    setIsCreateFormValid(isValid);
  }, []);

  const handleRateVersionReady = useCallback((getter: () => number | undefined) => {
    getRateVersionRef.current = getter;
  }, []);

  const columns: TableGridBaseColDef<QuoteRating>[] = useMemo(
    () => [
      {
        field: 'rateVersion',
        headerName: 'Rate Version',
        width: 100,
        sortable: false,
        renderCell: ({ row }) => {
          const rateChangeAnalysis = row.rateChangeAnalysis;
          if (!rateChangeAnalysis) return 'N/A';

          return `${rateChangeAnalysis.currentRateVersion} → ${rateChangeAnalysis.comparedToRateVersion}`;
        },
      },
      {
        field: 'ratedPremium',
        headerName: 'Rated Premium',
        width: 220,
        flex: 0.6,
        sortable: false,
        renderCell: ({ row }) => {
          const rateChangeAnalysis = row.rateChangeAnalysis;
          if (!rateChangeAnalysis) return 'N/A';

          const currentPremium = formatCurrency(rateChangeAnalysis.currentVersionRatedPremium);
          const comparedPremium = formatCurrency(rateChangeAnalysis.comparedToVersionRatedPremium);

          return (
            <Box sx={{ fontSize: '0.875rem' }}>
              {currentPremium} → {comparedPremium}
            </Box>
          );
        },
      },
      {
        field: 'ratedPremiumChange',
        headerName: 'Change',
        flex: 0.5,
        width: 80,
        sortable: false,
        renderCell: ({ row }) => {
          const rateChangeAnalysis = row.rateChangeAnalysis;
          if (!rateChangeAnalysis) return 'N/A';

          const percentage = rateChangeAnalysis.ratedPremiumPercentageChange || 0;
          return (
            <Box
              sx={{
                color: percentage >= 0 ? 'success.light' : 'error.dark',
                fontWeight: 'bold',
                fontSize: '0.875rem',
              }}
            >
              {formatPercentage(percentage)}
            </Box>
          );
        },
      },
      {
        field: 'dateRan',
        headerName: 'Date Ran',
        width: 180,
        flex: 0.6,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.createdAt) return '-';
          return formatDateToTimezone(row.createdAt, TwelveHourDateFormat);
        },
      },
      {
        field: 'details',
        headerName: 'Details',
        width: 80,
        sortable: false,
        renderCell: ({ row }) => {
          const rateChangeAnalysis = row.rateChangeAnalysis;
          if (!rateChangeAnalysis || !rateChangeAnalysis.details) return '-';

          const hasDetails =
            rateChangeAnalysis.details && rateChangeAnalysis.details !== '' && rateChangeAnalysis.details !== 'null';

          if (!hasDetails || Object.keys(rateChangeAnalysis.details).length === 0) return '-';

          return (
            <Button
              variant='text'
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(JSON.stringify(rateChangeAnalysis?.details));
              }}
              sx={{ fontSize: '0.75rem', minWidth: 'auto', padding: '0' }}
            >
              View
            </Button>
          );
        },
      },
    ],
    [formatPercentage, handleViewDetails],
  );

  const useSearchQuoteRatingsWrapper = (params: QuoteRatingSearchRequestQuery) => {
    return useSearchQuoteRatingsNonInfinite(quoteId, true, {
      ...params,
      hasRateChangeAnalysis: true,
      sort: '-createdAt',
    });
  };

  const isListView = view === 'list';

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={isListView ? onClose : undefined}
        size='large'
        title={isListView ? `Rate Change Analysis of ${policyNumber ?? ''}` : 'New Rate Change Analysis'}
        primaryBtnText={isListView ? 'Close' : 'Create New'}
        primaryBtnAction={isListView ? onClose : handleCreateSubmit}
        isPrimaryBtnDisabled={isListView ? isCreating || isPolling : !isCreateFormValid || isCreating || isPolling}
        isPrimaryBtnLoading={!isListView && (isCreating || isPolling)}
        {...(isListView
          ? {
              tertiaryBtnText: 'Create New',
              isTertiaryBtnDisabled: isCreating || isPolling,
              tertiaryBtnAction: () => setView('create'),
              tertiaryBtnLoading: isCreating || isPolling,
            }
          : {
              tertiaryBtnText: '\u2190 Go Back',
              tertiaryBtnAction: () => setView('list'),
            })}
      >
        {isListView ? (
          <Box sx={{ p: 3, minHeight: '400px' }}>
            <Table<TRateChangeTable>
              ariaLabel='rate-change-analysis-table'
              columns={columns}
              onLoadData={useSearchQuoteRatingsWrapper}
              hidePagination={false}
              noResultsConfig={{
                noResultsMessage: 'No rate change analysis records found.',
              }}
              rowConfig={{
                onRowClick: ({ row }) => handleRatingClick(row),
              }}
              sxProps={{
                dataGrid: {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
        ) : (
          <NewRateChangeAnalysisContent
            quote={quote}
            onFormValidityChange={handleFormValidityChange}
            onRateVersionReady={handleRateVersionReady}
            isDisabled={isCreating || isPolling}
          />
        )}
      </Dialog>

      {showRatingModal && selectedRating && (
        <RatingSummaryModal
          quoteId={quoteId}
          isOpen={showRatingModal}
          onCloseModal={handleCloseRatingModal}
          ratingResult={selectedRating}
        />
      )}

      {showDetailsModal && detailsData && (
        <Dialog
          open={showDetailsModal}
          onClose={handleCloseDetailsModal}
          size='large'
          title='Rate Change Analysis Details'
          primaryBtnText='Close'
          primaryBtnAction={handleCloseDetailsModal}
        >
          <Box sx={{ p: 3, minHeight: '400px' }}>
            <LazyJsonViewer
              value={detailsData}
              defaultInspectDepth={3}
              objectSortKeys
              sx={{
                overflowY: 'auto',
                fontSize: '12px',
                fontWeight: 300,
                backgroundColor: 'white',
                maxHeight: '500px',
              }}
            />
          </Box>
        </Dialog>
      )}
    </>
  );
};
